import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { ModalError } from '@vabembarqueja/embarque-ja-shared-components';
import { addDays, isAfter } from 'date-fns';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CarteiraService } from 'src/services';
import FileService from 'src/services/File.service';

import { useUserContext } from '../../../contexts/User.context';
import {
  FILTRO_TRANSACOES_DEFAULT,
  FiltroTransacoes,
  filtroTransacoesFormModelSchema
} from '../../../models/FiltroTransacoes.model';
import { TipoTransacao } from '../../../models/Transacao.model';
import { TipoUsuario } from '../../../models/Usuario';
import { Colors } from '../../../shared/colors';
import FilterFormLayout from '../../components/FilterFormLayout';
import { AutoComplete } from '../../components/Form/AutoComplete';
import DatePicker from '../../components/Form/DatePicker';
import { Input } from '../../components/Form/Input';
import { PatternInput } from '../../components/Form/PatternInput';

type TipoTransacaoSelect = {
  label: string;
  value: TipoTransacao;
};

interface Props {
  onClose: (filtroTransacoes: FiltroTransacoes) => void;
  tiposTransacao: TipoTransacaoSelect[];
  filtroTransacoes: FiltroTransacoes;
  permitirFiltrarUsuarios?: boolean;
  tab: number;
}

const FiltrarTransacoes = ({
  onClose,
  tiposTransacao,
  filtroTransacoes,
  permitirFiltrarUsuarios = false,
  tab
}: Props) => {
  const { userClaim } = useUserContext();
  const [error, setError] = useState(false);
  const usuarioPJ = userClaim === TipoUsuario.PESSOA_JURIDICA;

  const mostrarCampoUsuario = permitirFiltrarUsuarios && usuarioPJ;

  const form = useForm<FiltroTransacoes>({
    defaultValues: filtroTransacoes ?? FILTRO_TRANSACOES_DEFAULT,
    resolver: yupResolver(filtroTransacoesFormModelSchema)
  });

  const filtrarTransacoes = (filtros: FiltroTransacoes) => {
    const filtroSemValoresVazios = Object.fromEntries(
      Object.entries(filtros).filter(([_, v]) => v)
    );
    onClose(filtroSemValoresVazios);
  };

  const limparFiltros = () => {
    form.reset(FILTRO_TRANSACOES_DEFAULT);
  };

  const limparCamposVazios = (filtros: FiltroTransacoes) => {
    return Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
  };

  const handleCloseModalError = () => {
    setError(false);
  };

  const downloadArquivo = () => {
    const filtros = limparCamposVazios(form.getValues());

    CarteiraService.downloadTransacoes(filtros, tab)
      .then(({ file, contentType, contentDisposition }) => {
        let fileName = 'download';
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }

        const blob = new Blob([file], { type: contentType ? String(contentType) : undefined });
        FileService.downloadBlob(blob, fileName);
      })
      .catch((err) => {
        console.log(err);
        setError(true);
      });
  };

  const dataFim = form.watch('dataFinal') ?? (null as any);
  const dataInicio = form.watch('dataInicio') ?? (null as any);

  return (
    <>
      <FormProvider {...form}>
        <FilterFormLayout
          onClear={limparFiltros}
          onSubmit={form.handleSubmit(filtrarTransacoes)}
          onExportCsv={downloadArquivo}
          exportDisabled={!dataInicio || !dataFim || isAfter(dataInicio, dataFim)}
        >
          <Grid item xs={12}>
            <AutoComplete
              name="tipoTransacao"
              control={form.control}
              label="Tipo de transação"
              placeholder="Selecione o tipo de transação"
              options={tiposTransacao}
            />
          </Grid>

          {mostrarCampoUsuario && (
            <Grid item xs={12} sm={6}>
              <Input
                name="usuario"
                label="Usuário"
                placeholder="Nome de usuário"
                control={form.control}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>
          )}

          {mostrarCampoUsuario && (
            <Grid item xs={12} sm={6}>
              <PatternInput
                label="CPF"
                name="cpf"
                control={form.control}
                placeholder="CPF"
                format="###.###.###-##"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Input
              name="idAutorizacao"
              control={form.control}
              label="Número de autorização"
              placeholder="Número de autorização"
              inputProps={{ maxLength: 150 }}
            />
          </Grid>

          <Grid item xs={12} mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography color={Colors.text.label} fontSize={15} fontWeight={600}>
                Período das transações
              </Typography>
              <Divider
                flexItem
                sx={{ alignSelf: 'center', borderColor: Colors.border.divider, flex: 1 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              name="dataInicio"
              control={form.control}
              label="Data de início"
              placeholder="Data de início"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              minDate={dataInicio}
              maxDate={
                isAfter(new Date(), addDays(dataInicio, 30)) ? addDays(dataInicio, 30) : new Date()
              }
              name="dataFinal"
              control={form.control}
              label="Data final"
              placeholder="Data final"
            />
          </Grid>
        </FilterFormLayout>
      </FormProvider>
      <ModalError
        open={error}
        setOpen={setError}
        message={
          'Não encontramos transações para o período informado. Verifique as datas e tente novamente.'
        }
        onClose={handleCloseModalError}
      />
    </>
  );
};

export default FiltrarTransacoes;
